import React, { useContext, useEffect, useRef, useState } from 'react'
import ListGroupItem from './ListGroupItem'
import { AppContext } from '../../../context'
import ImrayLogo from '../../../assets/img/brand/Imray_Logo_2010.png'
import Slide from '../../ui/Slide'
import { filters } from '../FilterPopover'
import { API } from '../../../api'
import {
  conjurePromises,
  fixValues,
  stringIsNullOrEmpty
} from '../../../utils/mapHelpers.js'
import useImageUpload from '../../useImageUpload'
import { Spinner } from 'react-bootstrap'
import { some } from 'lodash'
import {
  CarouselImage,
  DetaildSourceLinkButtonPropsType,
  FacilityItem
} from '../../../typings/common' //TODO: Export this

//TODO: Export this

const setLocationType = function (type: string) {
  const trimmed = type?.replace(/^User/i, '')
  const noWhiteSpace = trimmed.replace(/\s/g, '')

  switch (noWhiteSpace) {
    case 'Anchorage':
      return 'Anchorage'
    case 'Area':
      return 'Area'
    case 'HarbourWithYachtFacilities':
      return 'Harbour With Yacht Facilities'
    case 'Mooring':
      return 'Mooring Buoys'
    case 'OtherHarbour':
      return 'Harbour with Limited Access'
    case 'YachtMarina':
      return 'Yacht Marina'
    case 'CulturalInformation':
      return 'Cultural Information'
    case 'Almanac':
      return 'Almanac'
    default:
      return type ?? ''
  }
}

const getLocationIconFileName = function (type: string) {
  const trimmed = type.replace(/^User/i, '')

  const locationTypes = [
    'Anchorage',
    'Area',
    'HarbourwithYachtFacilities',
    'Mooring',
    'OtherHarbour',
    'YachtMarina',
    'CulturalInformation',
    'Almanac'
  ]

  if (trimmed === 'HarbourwithYachtFacilities') {
    return 'HarbourWithYachtFacilities'
  }

  if (locationTypes.includes(trimmed)) {
    return type
  }

  return ''
}

const allFacilities: FacilityItem[] = []
filters.forEach(filter => {
  filter.items.forEach(facility => allFacilities.push(facility))
})

const PlaceDetailedPopover: React.FC = () => {
  const subscriptionUrl = `${process.env.REACT_APP_SUBSCRIPTIONS_URL}booksSubscriptions/`
  const communityStandardsUrl = `${process.env.REACT_APP_IMRAY_URL}community-standards/`
  const imrayURL = process.env.REACT_APP_IMRAY_URL

  const {
    detailsOverlayData,
    userSubscriptionStatus,
    handleBookmarks,
    onEnlargeModalOpen,
    activePopover,
    userData,
    setDetailsOverlayData,
    bookmarksLoading
  } = useContext(AppContext)
  const { hasAccess, props, propsWithoutLinks } = detailsOverlayData

  const {
    bookName = '',
    name,
    properties,
    bookAuthors = [],
    isUserGeneratedLocation = false
  } = props

  const [carouselImages, setCarouselImages] = useState([])

  useEffect(() => {
    const addCarouselImages = () => {
      if (!properties?.images) return

      const images = properties.images.map(
        (img: CarouselImage, i: number) => `${imrayURL}${img.url}`
      )
      setCarouselImages(images)
    }

    addCarouselImages()
  }, [properties?.images])

  const { authorImageURL, bookCoverURL, bookURL } = propsWithoutLinks

  const [isExpanded, setIsExpanded] = useState(false)
  const [userProfileData, setUserProfileData] = useState({
    firstName: '',
    lastName: '',
    profileImage: '',
    isLoaded: false
  })

  const [selectedTab, setSelectedTab] = useState('information')
  const locationType = setLocationType(properties?.locationTypes?.[0] ?? 'Area')
  const [numImages, setNumImages] = useState(0)
  const [commentBody, setCommentBody] = useState('')
  const [isCommentSectionActive, setIsCommentSectionActive] = useState(false)
  const hasImages = numImages > 0
  const detaildSourceLinkButtonProps: DetaildSourceLinkButtonPropsType = {}
  let showDetailedSourceLinkButton = false
  const {
    open: openWidget,
    uploadedImages,
    base64Images,
    removeImage,
    resetImages
  } = useImageUpload()

  //Merge all facilities into one big collection.
  const facilitiesCollection: string[] = properties
    ? new Array(0).concat(
        properties.transportLinks ?? [],
        properties?.marinaFacilities ?? [],
        properties?.shoppingFacilities ?? [],
        properties?.other ?? [],
        properties?.boatyardSupportServices ?? []
      )
    : []

  //Create a record to use for data mapping and displaying the icons.
  const facilititesRecord = facilitiesCollection.reduce((acc, item, index) => {
    acc[item] = item
    return acc
  }, {} as Record<string, string>)

  const commentBodyRef = useRef<HTMLTextAreaElement>(null)
  const panelBodyRef = useRef<HTMLDivElement>(null)
  const isBookmarked = userSubscriptionStatus && props.bookmarkId //Check if it is bookmarked from props
  const hasAccessToComments = true
  const isActive = activePopover === 'place-detailed'

  useEffect(() => {
    if (panelBodyRef.current) {
      panelBodyRef.current.scrollTop = 0
    }
  }, [isActive, props.id, hasImages])

  useEffect(() => {
    setSelectedTab('information')
    resetImages()
  }, [props.id, hasImages])

  useEffect(() => {
    ;(async () => {
      if (hasAccess) {
        let count = 0

        for (let i = 1; i <= carouselImages.length; i++) {
          const exists = await new Promise(resolve => {
            const img = new Image()
            let imageUrl: string = carouselImages && carouselImages[count]
            // We have to extract the url with a regex because the url comes like this:
            // "/media/aakpf4h2/mfcp_marines_de_<a href='http://cogolin-2-e0857d22-5d93-4ca5-bb42-52863e07f79f.jpg' target='_blank'>cogolin-2-e0857d22-5d93-4ca5-bb42-52863e07f79f.jpg</a>"

            const urlRegEx: RegExp = /https?:\/\/[^\s'"]+/g
            const extractedUrls = imageUrl && imageUrl.match(urlRegEx)

            img.src = extractedUrls?.[0] ?? ''
            img.onload = () => {
              resolve(true)
            }
            img.onerror = () => {
              resolve(false)
            }
          })

          if (exists) {
            count++
            if (count === carouselImages.length) {
              setNumImages(count)
            }
          } else {
            if (count === 0 && i === 1) {
              // If the first image fails to load, assume that no images exist
              setNumImages(0)
            } else {
              setNumImages(count)
            }
            // Break out of the loop when an error occurs
            break
          }
        }
      } else {
      }
    })()
  }, [hasAccess, carouselImages])

  const locationIconFileName = getLocationIconFileName(
    properties?.locationTypes?.[0]?.replace(/\s/g, '') ?? 'Area'
  )

  if (!stringIsNullOrEmpty(bookURL)) {
    // Set Imray book shop URL
    detaildSourceLinkButtonProps.href = bookURL
    detaildSourceLinkButtonProps.target = '_blank'
    showDetailedSourceLinkButton = true
  }

  let tooltipText =
    'Join Explore with Imray & complete your profile to access feedback'
  let showTooltip = true

  if (hasAccess) {
    if (hasAccessToComments) {
      tooltipText = ''
      showTooltip = false
    }
  }

  let userName = ''
  let bookmarkSubtitle = bookAuthors[0]
  let authorImageHtml = (
    <img id="details-default-image" src={bookCoverURL ?? ImrayLogo} />
  )
  let bookTitleHtml = (
    <div id="details-source-title" className="bookmark-title">
      {bookName}
    </div>
  )
  if (isUserGeneratedLocation) {
    if (userProfileData.isLoaded && userProfileData.profileImage) {
      userName = `${userProfileData.firstName} ${userProfileData.lastName}`
      authorImageHtml = (
        <img
          id="details-source-image-cover"
          alt={userName}
          title={userName}
          src={userProfileData.profileImage}
        />
      )
      bookmarkSubtitle = `Added by ${userName}`
    }
  } else if (!stringIsNullOrEmpty(authorImageURL)) {
    const hrefProps = {
      href: '',
      target: ''
    }
    if (!stringIsNullOrEmpty(authorImageURL)) {
      hrefProps.href = authorImageURL
      hrefProps.target = '_blank'
    } else if (!stringIsNullOrEmpty(bookURL)) {
      hrefProps.href = bookURL
      hrefProps.target = '_blank'
    }

    authorImageHtml = (
      <a id="details-source-image-anchor" {...hrefProps}>
        <img
          id="details-source-image"
          alt={bookAuthors[0]}
          title={bookAuthors[0]}
          src={authorImageURL}
        />
      </a>
    )

    const titleHrefProps = {
      href: '',
      target: ''
    }

    if (!stringIsNullOrEmpty(bookURL)) {
      titleHrefProps.href = bookURL
      titleHrefProps.target = '_blank'
    }

    bookTitleHtml = (
      <div id="details-source-title" className="bookmark-title">
        <a id="details-source-title-anchor" {...titleHrefProps}>
          {bookName}
        </a>
      </div>
    )
  }

  function onReportClick(commentId: string) {
    // el.closest('.ug-post-interactions')
    //   .find('.ug-post-more-button')
    //   .dropdown('hide')

    const formUrl = `https://share.hsforms.com/1HKM4KvLMRLOF0TvIzhPwrw4udgz?TICKET.commentId=${commentId}`
    const win = window.open(formUrl, '_blank')
    if (win) {
      win.focus()
    } else {
      alert('Please allow popups for this website')
    }
  }

  async function postLocationComment() {
    const text = commentBody

    let images: string[] = base64Images

    if (!text) {
      return commentBodyRef.current?.focus()
    }

    try {
      const promises = await conjurePromises(images, {
        method: 'POST',
        endpoint: '/upload-image',
        dynamicProp: 'image_binary'
      })

      const uploadedImages: string[] = promises.map(
        image => image.data.message.key
      )

      await API.post('/feedback', {
        feedback: text,
        images: uploadedImages
      })
      setCommentBody('')
      resetImages()
    } catch (error) {
      setCommentBody('')
      resetImages()
      console.log(`Error when posting feedback - ${error}`)
    }

    setIsCommentSectionActive(false)
  }

  const [imageElements, setImageElements] = useState<any>([])

  useEffect(() => {
    const controller = new AbortController()
    const signal = controller.signal

    if (hasAccess && carouselImages.length > 0 && !signal.aborted) {
      const loadImages = () => {
        const images = carouselImages.map((img, i) => {
          let imageUrl: string = carouselImages[i]

          const captionHeader = null //httpRequest.getResponseHeader('x-ms-meta-caption');
          const caption = !stringIsNullOrEmpty(captionHeader)
            ? captionHeader
            : ''

          return (
            <img
              className="expandable-image location-details-image"
              alt={caption!}
              title={caption!}
              src={imageUrl}
              onClick={() => onEnlargeModalOpen(imageUrl, caption || '')}
              key={i}
            />
          )
        })

        setImageElements(images)
      }
      loadImages()
    }

    return () => controller.abort()
  }, [carouselImages, hasAccess])
  return (
    <AppContext.Consumer>
      {({ activePopover, onTogglePopover }) => (
        <Slide
          id="details-overlay"
          className={`overlay overlay-right ${hasImages ? 'has-images' : ''} ${
            isExpanded ? 'expanded' : ''
          } hidden`}
          isVisible={activePopover === 'place-detailed'}
        >
          <>
            <div className="card-header">
              <div className="d-flex justify-content-between">
                <div id="card-header-left">
                  <div id="details-bookmark-button-container">
                    <a
                      id="details-bookmark-button"
                      className="btn-pill panel-button"
                      onClick={() =>
                        handleBookmarks(
                          isBookmarked ? 'remove' : 'add',
                          {
                            book_cover_url: props.bookCoverURL,
                            content_id: props.id ?? '',
                            id: props.bookmarkId ?? '',
                            content_name: props.name,
                            thumbnail: '',
                            user_id: userData?.id ?? '',
                            route: {
                              path: props.route?.path
                            }
                          },
                          async () => {
                            try {
                              if (!props || !props?.id) return

                              //TODO: Should be ported in a hook somewhere, as it's used in multiple places
                              const contentRes = await API.get('/content', {
                                params: {
                                  id: props?.id
                                }
                              })

                              const propsString = JSON.stringify(
                                contentRes?.data?.message ?? null,
                                fixValues
                              )

                              const propsWithoutLinks = JSON.parse(propsString)
                              const newProps = JSON.parse(propsString)

                              setDetailsOverlayData({
                                props: newProps,
                                propsWithoutLinks,
                                hasAccess
                              })
                            } catch (e) {
                              console.error(`Error fetching content: ${e}`)
                            }
                          }
                        )
                      }
                    >
                      <span
                        id="details-bookmark-button-icon"
                        className="material-icons"
                      >
                        {bookmarksLoading && (
                          <Spinner
                            animation="border"
                            size="sm"
                            variant="primary"
                          />
                        )}
                        {isBookmarked && !bookmarksLoading && 'bookmark'}
                        {!isBookmarked &&
                          !bookmarksLoading &&
                          'bookmark_border'}
                      </span>
                      <span className="pill-label">
                        {isBookmarked ? 'Bookmarked' : 'Bookmark'}
                      </span>
                    </a>
                  </div>
                </div>
                <div id="card-header-right">
                  {!isExpanded ? (
                    <div id="details-expand-container">
                      <a
                        id="details-expand-button"
                        className="btn-pill no-fill panel-button"
                        onClick={() => setIsExpanded(true)}
                      >
                        <span className="material-icons">fullscreen</span>
                        <span className="pill-label">Expand</span>
                      </a>
                    </div>
                  ) : (
                    <div
                      id="details-minimise-container"
                      style={{ display: 'block' }}
                    >
                      <a
                        id="details-minimise-button"
                        className="btn-pill no-fill panel-button"
                        onClick={() => setIsExpanded(false)}
                      >
                        <span className="material-icons">fullscreen_exit</span>
                        <span className="pill-label">Minimise</span>
                      </a>
                    </div>
                  )}
                  <div>
                    <a
                      className="close-button btn-pill no-fill no-label"
                      onClick={() => {
                        onTogglePopover('')
                      }}
                    >
                      <span className="material-icons">clear</span>
                    </a>
                  </div>
                </div>
              </div>
              {carouselImages.length > 0 &&
                carouselImages.length === numImages && (
                  <div id="contentImages">
                    <div className="locationImages-collection">
                      {imageElements}
                    </div>
                  </div>
                )}
            </div>
            <div className="panel-body" ref={panelBodyRef}>
              {/* <div id="location-feature"></div> */}
              <div className="card-body">
                <h2 id="details-title">{name}</h2>
                <div id="details-group">
                  <div id="details-pin-type">
                    {locationIconFileName && (
                      <img
                        src={require(`../../../assets/img/icons/${locationIconFileName}.svg`)}
                        alt="${locationType}"
                        title="${locationType}"
                      />
                    )}
                  </div>
                  <div id="details-pin-info">
                    <div id="details-category" className="spaced-out">
                      {locationType}
                    </div>
                    {/* <div id="details-location" className="spaced-out">
                      {location ?? ''}
                    </div> */}
                  </div>
                </div>
                {!hasAccess && (
                  <div id="unsubscribed-container">
                    <h3 id="unsubscribed-title">
                      Join Explore with Imray to view cruising information and
                      contribute data
                    </h3>
                    <p id="unsubscribed-sub-title">
                      In order to view cruising information, view and add
                      comments to existing information, and add locations to the
                      platform please click the join button and select a
                      membership.
                    </p>
                    <a
                      id="unsubscribed-subscribe-button"
                      className="btn btn-primary"
                      href={subscriptionUrl}
                    >
                      Join
                    </a>
                  </div>
                )}
                {/*
                <div id="contentImages" className="details-section-body">
                  <div id="" className="locationImages-collection"></div>
                </div> */}

                {/* Tab Navigation */}
                {hasAccess && (
                  <ul className="nav nav-tabs">
                    <li className="nav-item">
                      <a
                        className={`nav-link ${
                          selectedTab === 'information' ? 'active' : ''
                        }`}
                        onClick={() => setSelectedTab('information')}
                        style={{
                          cursor: 'pointer'
                        }}
                      >
                        <span className="material-icons">menu_book</span>{' '}
                        Information
                      </a>
                    </li>
                    <li
                      id="community-opinion-tab"
                      className={`nav-item ${
                        showTooltip ? 'tooltip-container' : ''
                      }`}
                    >
                      <span
                        id="community-opinion-tooltip-text"
                        className="tooltiptext"
                      >
                        {tooltipText}
                      </span>
                      <a
                        className={`nav-link ${tooltipText ? 'disabled' : ''} ${
                          selectedTab === 'community' ? 'active' : ''
                        }`}
                        onClick={() => {
                          if (!tooltipText.length) {
                            setSelectedTab('community')
                          }
                        }}
                        style={{
                          cursor: tooltipText ? '' : 'pointer'
                        }}
                      >
                        <span className="material-icons">comment</span> Feedback
                      </a>
                    </li>
                  </ul>
                )}

                {/* Tab Navigation */}
                <div className="tab-content">
                  {selectedTab === 'information' && (
                    <div className="tab-pane active" id="book-information">
                      {hasAccess && (
                        <div
                          id="accordion"
                          className="list-group list-group-flush"
                        >
                          <ListGroupItem title="Introduction">
                            {properties?.introduction?.markup}
                          </ListGroupItem>
                          <ListGroupItem title="Contact">
                            {properties?.harbourContacts &&
                              properties?.harbourContacts?.items && (
                                <div id="contentContact">
                                  {properties?.harbourContacts?.items?.map(
                                    i => (
                                      <div key={`key-${i}`}>
                                        {i?.content?.properties
                                          ?.contactName && (
                                          <div>
                                            {
                                              i?.content?.properties
                                                ?.contactName
                                            }
                                          </div>
                                        )}
                                        {i?.content?.properties
                                          ?.phoneNumber && (
                                          <div>
                                            {i?.content?.properties
                                              ?.countryPhoneCode +
                                              i?.content?.properties
                                                ?.phoneNumber}
                                          </div>
                                        )}
                                        {i?.content?.properties?.email && (
                                          <div
                                            dangerouslySetInnerHTML={{
                                              __html:
                                                i?.content?.properties?.email
                                            }}
                                          />
                                        )}
                                        {i?.content?.properties?.radio && (
                                          <div>
                                            Radio:{' '}
                                            {i?.content?.properties?.radio}
                                          </div>
                                        )}
                                        {i?.content?.properties?.website && (
                                          <div
                                            dangerouslySetInnerHTML={{
                                              __html:
                                                i?.content?.properties?.website
                                            }}
                                          />
                                        )}
                                      </div>
                                    )
                                  )}
                                </div>
                              )}
                          </ListGroupItem>
                          <ListGroupItem title="Outer Approach">
                            {properties?.outerApproach?.markup}
                          </ListGroupItem>

                          <ListGroupItem title="Approach" isOutsideBody={true}>
                            {props?.properties?.approachEntryDescription && (
                              <div
                                className="details-section-body"
                                dangerouslySetInnerHTML={{
                                  __html:
                                    properties?.approachEntryDescription.markup
                                }}
                              />
                            )}
                            {props?.properties?.approachNotes && (
                              <div className="list-group-item subsection">
                                <strong>Notes</strong>
                                <div
                                  id="contentApproachNotes"
                                  className="details-section-body"
                                  dangerouslySetInnerHTML={{
                                    __html: properties?.approachNotes.markup
                                  }}
                                />
                              </div>
                            )}
                            {props?.properties?.hazards && (
                              <div className="list-group-item subsection">
                                <strong>Danger</strong>
                                <div
                                  className="details-section-body"
                                  dangerouslySetInnerHTML={{
                                    __html: properties?.hazards?.markup
                                  }}
                                />
                              </div>
                            )}
                            {some([
                              properties?.navigationAidsGeneral,
                              properties?.navigationAidsLeadingMarks,
                              properties?.navigationAidsBuoysBeacons,
                              properties?.navigationAidsLights
                            ]) && (
                              <div className="list-group-item subsection">
                                <strong>Navigation Aids</strong>
                                <br />
                                <br />
                                {properties?.navigationAidsGeneral && (
                                  <div
                                    className="details-section-body"
                                    dangerouslySetInnerHTML={{
                                      __html:
                                        properties?.navigationAidsGeneral
                                          ?.markup
                                    }}
                                  />
                                )}
                                {properties?.navigationAidsLeadingMarks && (
                                  <div
                                    className="details-section-body"
                                    dangerouslySetInnerHTML={{
                                      __html:
                                        properties?.navigationAidsLeadingMarks
                                          ?.markup
                                    }}
                                  />
                                )}
                                {properties?.navigationAidsBuoysBeacons && (
                                  <div
                                    className="details-section-body"
                                    dangerouslySetInnerHTML={{
                                      __html:
                                        properties?.navigationAidsBuoysBeacons
                                          ?.markup
                                    }}
                                  />
                                )}
                                {properties?.navigationAidsLights && (
                                  <div
                                    className="details-section-body"
                                    dangerouslySetInnerHTML={{
                                      __html:
                                        properties?.navigationAidsLights?.markup
                                    }}
                                  />
                                )}
                              </div>
                            )}
                          </ListGroupItem>

                          <ListGroupItem title="Facilities">
                            {properties?.facilities ||
                            allFacilities.some(
                              c => facilititesRecord[c.key]
                            ) ? (
                              <>
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: properties?.facilities.markup
                                  }}
                                />
                                <ul
                                  style={{ listStyle: 'none' }}
                                  id="iconsFacilities"
                                >
                                  {allFacilities
                                    .filter(
                                      facility =>
                                        facilititesRecord[facility.key]
                                    )
                                    .map(facility => (
                                      <li key={facility.key}>
                                        <img
                                          id={facility.key?.toLocaleLowerCase()}
                                          className="filter-image"
                                          src={facility.imageSrc}
                                        />
                                        <div>{facility.label}</div>
                                      </li>
                                    ))}
                                </ul>
                              </>
                            ) : null}
                          </ListGroupItem>

                          {some([
                            properties?.data?.markup,
                            properties?.shelter?.markup,
                            properties?.berth?.markup,
                            properties?.shelter?.berth,
                            properties?.authorities?.markup,
                            properties?.note?.markup,
                            properties?.chargeBand?.markup,
                            properties?.anchorage?.markup
                          ]) && (
                            <ListGroupItem title="Moorings and Anchorages">
                              {properties?.data?.markup && (
                                <>
                                  <div>
                                    Data:
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: properties?.data?.markup
                                      }}
                                    />
                                  </div>
                                  <br />
                                </>
                              )}
                              {properties?.shelter?.markup && (
                                <>
                                  <div>
                                    Shelter:{' '}
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: properties?.shelter?.markup
                                      }}
                                    />
                                  </div>
                                  <br />
                                </>
                              )}
                              {properties?.berth?.markup && (
                                <>
                                  <div>
                                    Berth:{' '}
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: properties?.berth?.markup
                                      }}
                                    />
                                  </div>
                                  <br />
                                </>
                              )}
                              {properties?.authorities?.markup && (
                                <>
                                  <div>
                                    Authorities:{' '}
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: properties?.authorities?.markup
                                      }}
                                    />
                                  </div>
                                  <br />
                                </>
                              )}
                              {properties?.note?.markup && (
                                <>
                                  <div>
                                    Note:{' '}
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: properties?.note?.markup
                                      }}
                                    />
                                  </div>
                                  <br />
                                </>
                              )}

                              {properties?.chargeBand?.markup && (
                                <>
                                  <div>
                                    Charge Band:{' '}
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: properties?.chargeBand?.markup
                                      }}
                                    />
                                  </div>
                                  <br />
                                </>
                              )}
                              {properties?.anchorage?.markup && (
                                <>
                                  <div>Anchorage:</div>
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: properties?.anchorage?.markup
                                    }}
                                  />
                                </>
                              )}
                            </ListGroupItem>
                          )}

                          {properties?.general && (
                            <ListGroupItem title="General Information">
                              {properties?.general?.markup}
                            </ListGroupItem>
                          )}

                          {properties?.note && (
                            <ListGroupItem title="Notes">
                              {properties?.note?.markup}
                            </ListGroupItem>
                          )}
                        </div>
                      )}
                      <br />
                      {hasAccess && (
                        <>
                          {/*        <div id="details-info" className="spaced-out">
                            {props.information && (
                              <>
                                <i>Charts and LOCODES:&nbsp;</i> + {information}
                              </>
                            )}
                            {name &&(
                              <>{name && <hr />}
                                <div id="details-info" className="spaced-out">
                                  {name}
                                </div>
                              </>
                            )}
                          </div>*/}
                        </>
                      )}
                      <div className="card-footer">
                        <div className="details-source-container">
                          <div className="details-source-image-author">
                            <div id="details-source-image-container">
                              {authorImageHtml}
                            </div>
                            <div id="details-source-reference">
                              {bookTitleHtml}
                              <div
                                id="details-source-author"
                                className="bookmark-subtitle"
                              >
                                {bookmarkSubtitle}
                              </div>
                            </div>
                          </div>
                          <div id="details-source-link">
                            {showDetailedSourceLinkButton && (
                              <a
                                id="details-source-link-button"
                                className="btn-pill panel-button"
                                {...detaildSourceLinkButtonProps}
                              >
                                <span
                                  id="details-source-link-button-icon"
                                  className="material-icons-outlined"
                                >
                                  shopping_bag
                                </span>
                                <span className="pill-label">Buy</span>
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedTab === 'community' && (
                    <div className="tab-pane" style={{ display: 'block' }}>
                      <div className="terms alert alert-primary">
                        <h5>We welcome your feedback.</h5>
                        <span>
                          If you have any updates, new information or have
                          spotted errors or omissions please add them below. For
                          more information please see our{' '}
                          <a
                            title="View our Community Standards"
                            href={communityStandardsUrl}
                          >
                            Community Standards
                          </a>
                          .
                        </span>
                      </div>
                      <div
                        className={`card-footer location-comments-footer ${
                          isCommentSectionActive ? 'input-active' : ''
                        }`}
                      >
                        {bookName && (
                          <div id="feedback-timestamp">{bookName}</div>
                        )}
                        <form id="add-location-comment" className={`clearfix`}>
                          <div className="form-group">
                            <textarea
                              name="comment-body"
                              rows={3}
                              maxLength={300}
                              placeholder="Add a comment..."
                              value={commentBody}
                              onChange={e => setCommentBody(e.target.value)}
                              ref={commentBodyRef}
                              onFocus={() => setIsCommentSectionActive(true)}
                            />
                            <div className="ug-post-add-comment">
                              <div className="location-comment-image-actions">
                                <div
                                  id="add-location-comment-images"
                                  className="btn-pill"
                                >
                                  <input
                                    id="uploadBtn"
                                    type="file"
                                    onChange={openWidget}
                                  />
                                  <label htmlFor="uploadBtn">
                                    <span className="material-icons-outlined">
                                      add_a_photo
                                    </span>
                                  </label>
                                </div>
                                {!uploadedImages.length && (
                                  <span className="location-image-limit">
                                    Add upto 4 images
                                  </span>
                                )}
                                <div className="thumbnails">
                                  <ul className="cloudinary-thumbnails">
                                    {uploadedImages.map((image, index) => (
                                      <li
                                        key={index}
                                        className="cloudinary-thumbnail active"
                                      >
                                        <img src={image.thumbnail_url} />
                                        <a
                                          href="#"
                                          className="cloudinary-delete"
                                          onClick={() => {
                                            removeImage(index)
                                          }}
                                        >
                                          x
                                        </a>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                              <div>
                                <a
                                  id="add-location-comment-btn"
                                  href="#"
                                  className="btn-pill"
                                  onClick={e => {
                                    e.preventDefault()
                                    postLocationComment().then()
                                  }}
                                >
                                  <span className="material-icons">add</span>
                                  <span>Share</span>
                                </a>
                              </div>
                            </div>
                          </div>
                        </form>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        </Slide>
      )}
    </AppContext.Consumer>
  )
}

export default PlaceDetailedPopover
